import { Component, OnInit } from '@angular/core';
import { BookingService } from '../../core/services/booking.service';
import { BookingStats } from '../../core/services/booking.service';
import { BookingStatus } from '../../core/enums/booking-status.enum';
import { Booking } from '../../core/models/booking.model';
import { PaymentStatus } from '../../core/enums/payment-status.enum';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

@Component({
  selector: 'app-booking-management',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, FormsModule],
  templateUrl: './booking-management.component.html',
  styleUrls: ['./booking-management.component.css']
})
export class BookingManagementComponent implements OnInit {
  bookings: Booking[] = [];
  filteredBookings: Booking[] = [];
  bookingStats: BookingStats | null = null;
  currentRole: UserRole = UserRole.PASSENGER;
  isDriver = false;
  
  // Filter properties
  statusFilter: string = '';
  dateFilter: string = '';
  searchFilter: string = '';
  
  // Status options - expose enums as public properties for template
  bookingStatusEnum = BookingStatus; // Expose enum to template
  paymentStatusEnum = PaymentStatus; // Expose enum to template

  // Status options
  statusOptions = Object.values(BookingStatus);
  paymentStatusOptions = Object.values(PaymentStatus);

  isLoading = false;
  errorMessage = '';
  successMessage = '';
  featureAvailable = true;
  private bookingsLoaded = false;

  constructor(
    private bookingService: BookingService,
    private authService: AuthService,
    private featureFlags: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.setRoleContext();
    if (this.currentRole === UserRole.PASSENGER) {
      this.loadBookings();
      return;
    }
    this.featureAvailable = false;
    this.featureFlags.effectiveFeatures$.subscribe(states => {
      this.featureAvailable = states[FEATURE_KEYS.operatorBookingManagement] === true;
      if (this.featureAvailable && !this.bookingsLoaded) this.loadBookings();
    });
    this.featureFlags.featureError$.subscribe(problem => {
      if (problem?.featureKey === FEATURE_KEYS.operatorBookingManagement) {
        this.featureAvailable = false;
        this.errorMessage = problem.detail;
      }
    });
    this.featureFlags.loadEffectiveFeatures().subscribe({ error: () => this.featureAvailable = false });
  }

  loadBookings(): void {
    if (this.currentRole !== UserRole.PASSENGER && !this.featureAvailable) return;
    this.bookingsLoaded = true;
    this.isLoading = true;
    this.errorMessage = '';
    this.loadBookingStats();

    this.bookingService.getBookings().subscribe({
      next: (bookings) => {
        this.bookings = this.orderBookings(bookings);
        this.filteredBookings = [...this.bookings];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading bookings:', error);
        this.errorMessage = 'Unable to load bookings for your account right now.';
        this.isLoading = false;
      }
    });
  }

  loadBookingStats(): void {
    this.bookingService.getBookingStats().subscribe({
      next: stats => this.bookingStats = stats,
      error: error => {
        console.error('Unable to load booking stats:', error);
        this.bookingStats = null;
      }
    });
  }

  filterBookings(): void {
    this.filteredBookings = this.bookings.filter(booking => {
      let matches = true;
      
      // Filter by status
      if (this.statusFilter && this.statusFilter !== '') {
        matches = matches && this.getBookingStatus(booking) === this.statusFilter;
      }
      
      // Filter by date
      if (this.dateFilter) {
        const bookingDate = new Date(booking.bookingDate).toDateString();
        const filterDate = new Date(this.dateFilter).toDateString();
        matches = matches && bookingDate === filterDate;
      }
      
      // Filter by search term
      if (this.searchFilter && this.searchFilter.trim() !== '') {
        const searchTerm = this.searchFilter.toLowerCase();
        matches = matches && !!(
          booking.bookingID.toString().includes(searchTerm) ||
          booking.bookingReference?.toLowerCase().includes(searchTerm) ||
          booking.passengerName?.toLowerCase().includes(searchTerm) ||
          booking.passengerEmail?.toLowerCase().includes(searchTerm) ||
          booking.passengerPhone?.toLowerCase().includes(searchTerm) ||
          booking.user?.userName?.toLowerCase().includes(searchTerm) ||
          booking.user?.userEmail?.toLowerCase().includes(searchTerm) ||
          booking.tripName?.toLowerCase().includes(searchTerm) ||
          booking.trip?.tripName?.toLowerCase().includes(searchTerm) ||
          booking.trip?.route?.routeStartLocation?.toLowerCase().includes(searchTerm) ||
          booking.trip?.route?.routeEndLocation?.toLowerCase().includes(searchTerm)
        );
      }
      
      return matches;
    });
  }

  // Print ticket functionality
  printTicket(booking: Booking): void {
    const printContent = this.generateTicketContent(booking);
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ticket - Booking #${booking.bookingID}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
            .ticket { border: 2px solid #000; padding: 20px; max-width: 500px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 15px; margin-bottom: 20px; }
            .header h1 { margin: 0; color: #007bff; }
            .header p { margin: 5px 0; color: #666; }
            .section { margin-bottom: 15px; }
            .section h3 { margin: 0 0 10px 0; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
            .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
            .label { font-weight: bold; color: #555; }
            .value { color: #333; }
            .barcode { text-align: center; margin: 20px 0; font-family: 'Courier New', monospace; font-size: 24px; letter-spacing: 3px; }
            .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
            .qrcode { text-align: center; margin: 20px 0; }
            .qrcode-placeholder { display: inline-block; width: 150px; height: 150px; background: #f0f0f0; border: 1px dashed #ccc; line-height: 150px; color: #999; }
            @media print {
              .no-print { display: none; }
              body { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
              Print Ticket
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
              Close
            </button>
          </div>
          <script>
            // Auto-print option (uncomment if you want auto-print)
            // window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      this.errorMessage = 'Please allow popups to print the ticket.';
    }
  }

  // Generate HTML content for the ticket
  private generateTicketContent(booking: Booking): string {
    const trip = booking.trip;
    const user = booking.user;
    const route = trip?.route;
    
    const formatDate = (dateString: string) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return `
      <div class="ticket">
        <div class="header">
          <h1>RIYA SHUMA</h1>
          <p>Smart Transportation System</p>
          <p>Booking Confirmation</p>
        </div>
        
        <div class="section">
          <h3>Booking Information</h3>
          <div class="row">
            <span class="label">Booking ID:</span>
            <span class="value">#${booking.bookingID}</span>
          </div>
          <div class="row">
            <span class="label">Booking Date:</span>
            <span class="value">${formatDate(booking.bookingDate)}</span>
          </div>
          <div class="row">
            <span class="label">Status:</span>
            <span class="value" style="color: ${this.getStatusColor(booking.bookingStatus)}">
              ${this.getBookingStatus(booking).toUpperCase()}
            </span>
          </div>
          <div class="row">
            <span class="label">Payment Status:</span>
            <span class="value" style="color: ${this.getPaymentStatusColor(booking.paymentStatus)}">
              ${(booking.paymentStatus || 'unknown').toUpperCase()}
            </span>
          </div>
        </div>
        
        <div class="section">
          <h3>Trip Details</h3>
          <div class="row">
            <span class="label">Trip:</span>
            <span class="value">${trip?.tripName || 'N/A'}</span>
          </div>
          <div class="row">
            <span class="label">Route:</span>
            <span class="value">${route?.routeStartLocation || 'N/A'} → ${route?.routeEndLocation || 'N/A'}</span>
          </div>
          <div class="row">
            <span class="label">Departure:</span>
            <span class="value">${formatDate(trip?.tripStartDateTime || '')}</span>
          </div>
          <div class="row">
            <span class="label">Arrival:</span>
            <span class="value">${formatDate(trip?.tripEndDateTime || '')}</span>
          </div>
          <div class="row">
            <span class="label">Vehicle:</span>
            <span class="value">${trip?.vehicle?.vehicleRegistration || 'N/A'}</span>
          </div>
        </div>
        
        <div class="section">
          <h3>Passenger Information</h3>
          <div class="row">
            <span class="label">Name:</span>
            <span class="value">${this.getPassengerName(booking)}</span>
          </div>
          <div class="row">
            <span class="label">Email:</span>
            <span class="value">${this.getPassengerEmail(booking)}</span>
          </div>
          <div class="row">
            <span class="label">Seats Booked:</span>
            <span class="value">${this.getSeatCount(booking)}</span>
          </div>
        </div>
        
        <div class="section">
          <h3>Payment Details</h3>
          <div class="row">
            <span class="label">Total Amount:</span>
            <span class="value">R ${this.getBookingTotal(booking).toFixed(2)}</span>
          </div>
          <div class="row">
            <span class="label">Price per Seat:</span>
            <span class="value">R ${(this.getBookingTotal(booking) / Math.max(this.getSeatCount(booking), 1)).toFixed(2)}</span>
          </div>
          <div class="row">
            <span class="label">Payment Method:</span>
            <span class="value">${booking.paymentMethod || 'Not specified'}</span>
          </div>
        </div>
        
        ${booking.notes ? `
        <div class="section">
          <h3>Notes</h3>
          <p>${booking.notes}</p>
        </div>
        ` : ''}
        
        <div class="barcode">
          ║ ${booking.bookingID.toString().padStart(8, '0')} ║
        </div>
        
        <div class="qrcode">
          <div class="qrcode-placeholder">
            QR Code
          </div>
          <p>Scan for verification</p>
        </div>
        
        <div class="footer">
          <p>Thank you for choosing Riya Shuma!</p>
          <p>For inquiries: support@fleettrack.co.za | +27 11 123 4567</p>
          <p>Please arrive at least 30 minutes before departure</p>
          <p>Ticket generated: ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;
  }

  // Clear all filters
  clearFilters(): void {
    this.statusFilter = '';
    this.dateFilter = '';
    this.searchFilter = '';
    this.filterBookings();
  }

  // Calculate total revenue from filtered bookings
  getTotalRevenue(): number {
    if (!this.canManageBookings()) {
      return 0;
    }

    return this.filteredBookings
      .filter(booking => booking.paymentStatus === PaymentStatus.PAID)
      .reduce((total, booking) => total + this.getBookingTotal(booking), 0);
  }

  // Count bookings by status
  getCountByStatus(status: BookingStatus): number {
    if (status === BookingStatus.CONFIRMED && this.bookingStats?.confirmedBookings !== undefined) {
      return this.bookingStats.confirmedBookings;
    }

    if (status === BookingStatus.PENDING && this.bookingStats?.pendingBookings !== undefined) {
      return this.bookingStats.pendingBookings;
    }

    if (status === BookingStatus.CANCELLED && this.bookingStats?.cancelledBookings !== undefined) {
      return this.bookingStats.cancelledBookings;
    }

    return this.bookings.filter(booking => this.getBookingStatus(booking) === status).length;
  }

  // Update booking status
  updateBookingStatus(bookingId: number, status: BookingStatus): void {
    const request$ = status === BookingStatus.CANCELLED
      ? this.bookingService.cancelBookingById(bookingId)
      : this.bookingService.updateBookingStatus(bookingId, status);

    request$.subscribe({
      next: () => {
        this.successMessage = 'Booking status updated.';
        this.loadBookings();
      },
      error: (error) => {
        console.error('Error updating booking status:', error);
        this.errorMessage = 'Unable to update booking status. The API currently guarantees cancel-by-id, but may not support every status transition.';
      }
    });
  }

  // Update payment status
  updatePaymentStatus(bookingId: number, status: PaymentStatus): void {
    this.bookingService.updatePaymentStatus(bookingId, status).subscribe({
      next: () => {
        this.successMessage = 'Payment status updated.';
        this.loadBookings();
      },
      error: (error) => {
        console.error('Error updating payment status:', error);
        this.errorMessage = 'Unable to update payment status. This endpoint may not be available in the API yet.';
      }
    });
  }

  // Get status color for UI
  getStatusColor(status: BookingStatus | undefined): string {
    switch(status) {
      case BookingStatus.CONFIRMED: return '#28a745';
      case BookingStatus.PENDING: return '#ffc107';
      case BookingStatus.CANCELLED: return '#dc3545';
      case BookingStatus.NO_SHOW: return '#6c757d';
      default: return '#6c757d';
    }
  }

  // Get payment status color for UI
  getPaymentStatusColor(status: PaymentStatus | string | undefined): string {
    const normalized = String(status || '').toLowerCase();
    switch(normalized) {
      case PaymentStatus.PAID: return '#28a745';
      case PaymentStatus.PENDING: return '#ffc107';
      case PaymentStatus.FAILED: return '#dc3545';
      case PaymentStatus.REFUNDED: return '#17a2b8';
      case 'payatticketoffice': return '#2563eb';
      default: return '#6c757d';
    }
  }

  // Export bookings to CSV
  exportToCSV(): void {
    if (this.filteredBookings.length === 0) {
      this.errorMessage = 'No bookings to export.';
      return;
    }

    const headers = [
      'Booking ID', 'Passenger Name', 'Passenger Email', 'Trip Name',
      'Route', 'Departure Date', 'Seats', 'Total Price', 'Booking Status',
      'Payment Status', 'Payment Method', 'Booking Date'
    ];

    const csvData = this.filteredBookings.map(booking => [
      booking.bookingID,
      booking.passengerName || booking.user?.userName || '',
      booking.passengerEmail || booking.user?.userEmail || '',
      booking.trip?.tripName || '',
      `${booking.trip?.route?.routeStartLocation || ''} → ${booking.trip?.route?.routeEndLocation || ''}`,
      booking.trip?.tripStartDateTime ? new Date(booking.trip.tripStartDateTime).toLocaleDateString() : '',
      this.getSeatCount(booking),
      this.getBookingTotal(booking),
      this.getBookingStatus(booking),
      booking.paymentStatus || '',
      booking.paymentMethod || '',
      booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Send booking confirmation email
  sendConfirmationEmail(booking: Booking): void {
    if (!booking.user?.userEmail) {
      this.errorMessage = 'No email address available for this booking.';
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    // In a real app, you would call an API endpoint
    console.log(`Sending confirmation email for booking #${booking.bookingID} to ${booking.user.userEmail}`);
    
    // Mock API call
    setTimeout(() => {
      this.successMessage = `Confirmation email sent to ${booking.user?.userEmail}`;
    }, 1000);
  }

  // Add these methods to handle select changes
  onStatusChange(bookingId: number, event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const status = selectElement.value as BookingStatus;
    this.updateBookingStatus(bookingId, status);
  }

  onPaymentStatusChange(bookingId: number, event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const status = selectElement.value as PaymentStatus;
    this.updatePaymentStatus(bookingId, status);
  }

  // Refresh data
  refreshData(): void {
    this.loadBookings();
  }

  getSeatCount(booking: Booking): number {
    return booking.numberOfSeats || booking.seatsBooked || 0;
  }

  getBookingTotal(booking: Booking): number {
    return Number(booking.totalPrice || 0);
  }

  getBookingStatus(booking: Booking): BookingStatus {
    return booking.bookingStatus || BookingStatus.CONFIRMED;
  }

  getPassengerName(booking: Booking): string {
    return booking.passengerName || booking.user?.userName || 'N/A';
  }

  getPassengerEmail(booking: Booking): string {
    return booking.passengerEmail || booking.user?.userEmail || 'N/A';
  }

  getPassengerContact(booking: Booking): string {
    return booking.passengerEmail || booking.passengerPhone || booking.user?.userEmail || 'Contact not captured';
  }

  getTripName(booking: Booking): string {
    return booking.tripName || booking.trip?.tripName || 'N/A';
  }

  getOperatorName(booking: Booking): string {
    const source = booking as any;
    return booking.operatorName
      || booking.organisationName
      || booking.orgName
      || source.operatorCode
      || source.operator?.operatorName
      || source.operator?.name
      || source.operator?.orgName
      || source.organisation?.organisationName
      || source.organisation?.orgName
      || source.trip?.operatorName
      || source.trip?.organisationName
      || source.trip?.operator?.operatorName
      || source.trip?.operator?.name
      || source.trip?.organisation?.organisationName
      || source.trip?.organisation?.orgName
      || 'Operator not shown';
  }

  getOperatorLogo(booking: Booking): string {
    const source = booking as any;
    return booking.operatorLogoUrl
      || booking.orgUrl
      || source.operatorLogo
      || source.organisationLogo
      || source.orgLogo
      || source.operator?.operatorLogoUrl
      || source.operator?.orgUrl
      || source.operator?.logo
      || source.organisation?.operatorLogoUrl
      || source.organisation?.orgUrl
      || source.organisation?.orgLogo
      || source.trip?.operatorLogoUrl
      || source.trip?.orgUrl
      || source.trip?.operator?.operatorLogoUrl
      || source.trip?.operator?.orgUrl
      || source.trip?.organisation?.operatorLogoUrl
      || source.trip?.organisation?.orgUrl
      || '';
  }

  getTripDate(booking: Booking): string | null {
    return booking.tripStartTime || booking.trip?.tripStartDateTime || booking.trip?.tripStartTime || null;
  }

  getTripEndDate(booking: Booking): string | null {
    return booking.tripEndTime || booking.trip?.tripEndDateTime || booking.trip?.tripEndTime || null;
  }

  getRouteLabel(booking: Booking): string {
    const route = booking.trip?.route;
    if (!route) {
      return 'Route not linked';
    }

    return route.routeName || [route.routeStartLocation, route.routeEndLocation].filter(Boolean).join(' → ') || 'Route not linked';
  }

  getSeatNumbersLabel(booking: Booking): string {
    return booking.seatNumbers?.length ? booking.seatNumbers.join(', ') : 'Not assigned';
  }

  getPaymentLabel(booking: Booking): string {
    const status = String(booking.paymentStatus || '').trim();
    if (!status || status.toLowerCase() === PaymentStatus.PENDING) {
      return 'Pending';
    }

    if (status.toLowerCase() === 'payatticketoffice' || status.toLowerCase() === 'pay_at_ticket_office') {
      return 'Pay at ticket office';
    }

    return status.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  getStatusLabel(booking: Booking): string {
    const status = this.getBookingStatus(booking);
    if (status === BookingStatus.PENDING) return 'Pending';
    if (status === BookingStatus.CANCELLED) return 'Cancelled';
    if (status === BookingStatus.CONFIRMED) return 'Confirmed';
    return String(status || 'Unknown').replace(/_/g, ' ');
  }

  getTicketUrl(booking: Booking): string {
    const rawUrl = String(booking.eTicketUrl || '').trim();
    if (!rawUrl) {
      return '';
    }

    const token = this.extractTicketToken(rawUrl);
    return token ? `/tickets/${encodeURIComponent(token)}` : rawUrl;
  }

  getTicketAvailabilityLabel(booking: Booking): string {
    return this.getTicketUrl(booking) ? 'E-ticket available' : 'Use booking reference';
  }

  getUpcomingBookings(): Booking[] {
    const now = Date.now();
    return this.filteredBookings
      .filter(booking => {
        const start = this.getTripDate(booking);
        return start ? new Date(start).getTime() >= now : false;
      })
      .sort((a, b) => this.getTripTime(a) - this.getTripTime(b));
  }

  getUpcomingBookingCount(): number {
    return this.bookingStats?.upcomingBookings ?? this.getUpcomingBookings().length;
  }

  getPastBookingCount(): number {
    return this.bookingStats?.pastBookings ?? this.getPastBookings().length;
  }

  getUpcomingTripCount(): number {
    return this.bookingStats?.upcomingTrips ?? this.bookingStats?.upcomingBookings ?? this.getDistinctTripCount(this.getUpcomingBookings());
  }

  getPastTripCount(): number {
    return this.bookingStats?.pastTrips ?? this.bookingStats?.pastBookings ?? this.getDistinctTripCount(this.getPastBookings());
  }

  getTicketAvailableCount(): number {
    return this.filteredBookings.filter(booking => Boolean(this.getTicketUrl(booking))).length;
  }

  getOperatorCount(): number {
    return new Set(
      this.filteredBookings
        .map(booking => this.getOperatorName(booking))
        .filter(operator => operator && operator !== 'Operator not shown')
    ).size;
  }

  getNextBooking(): Booking | null {
    return this.getUpcomingBookings()[0] || null;
  }

  getPastBookings(): Booking[] {
    const now = Date.now();
    return this.filteredBookings
      .filter(booking => {
        const start = this.getTripDate(booking);
        return !start || new Date(start).getTime() < now;
      })
      .sort((a, b) => this.getTripTime(b) - this.getTripTime(a));
  }

  canManageBookings(): boolean {
    return this.currentRole === UserRole.OPERATOR
      || this.currentRole === UserRole.ADMIN
      || this.currentRole === UserRole.PLATFORM_EMPLOYEE;
  }

  isReadOnlyBookingView(): boolean {
    return !this.canManageBookings();
  }

  getPageTitle(): string {
    if (this.isDriver) {
      return 'Assigned Trip Bookings';
    }

    return this.canManageBookings() ? 'Booking Management' : 'My Bookings';
  }

  shouldShowPassengerContact(): boolean {
    return !this.isDriver;
  }

  private setRoleContext(): void {
    const currentUser = this.authService.getCurrentUser();
    this.currentRole = normalizeUserRole(currentUser?.userRoleName || currentUser?.userRole || currentUser?.userRoleID);
    this.isDriver = this.isDriverRole(currentUser);
  }

  private isDriverRole(user: any): boolean {
    const rawRole = String(user?.userRoleName || user?.userRole?.userRoleName || user?.userRole || '').trim().toLowerCase();
    return rawRole === 'driver' || rawRole === 'drivers' || Number(user?.userRoleID) === 5;
  }

  private orderBookings(bookings: Booking[]): Booking[] {
    const now = Date.now();
    return [...bookings].sort((a, b) => {
      const aTime = this.getTripTime(a);
      const bTime = this.getTripTime(b);
      const aUpcoming = aTime >= now;
      const bUpcoming = bTime >= now;

      if (aUpcoming !== bUpcoming) {
        return aUpcoming ? -1 : 1;
      }

      return aUpcoming ? aTime - bTime : bTime - aTime;
    });
  }

  private getTripTime(booking: Booking): number {
    const tripDate = this.getTripDate(booking);
    const bookingDate = booking.bookingDate;
    return new Date(tripDate || bookingDate || 0).getTime();
  }

  private getDistinctTripCount(bookings: Booking[]): number {
    return new Set(bookings.map(booking => booking.tripID || this.getTripName(booking))).size;
  }

  private extractTicketToken(url: string): string {
    const match = url.match(/\/tickets\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  }
}
